<template>
  <div class="history-page">
    <header class="page-heading">
      <div>
        <h4>核心股历史</h4>
        <p>用历史分钟行情重放动态核心算法，生成并查看某个交易日的盘中核心股变化。</p>
      </div>
      <b-badge variant="primary">dynamic_core_v3</b-badge>
    </header>

    <b-card class="control-card">
      <b-form inline @submit.prevent="generateHistory">
        <label class="mr-2">交易日</label>
        <b-form-input v-model="tradeDate" type="date" size="sm" required class="mr-3" />
        <label class="mr-2">间隔</label>
        <b-form-select v-model.number="intervalMinutes" :options="intervalOptions" size="sm" class="mr-3 interval-select" />
        <label class="mr-2">每题材核心股</label>
        <b-form-input v-model.number="coreLimit" type="number" min="1" max="3" size="sm" class="mr-3 limit-input" />
        <b-button type="submit" variant="danger" size="sm" :disabled="generating">
          <b-spinner v-if="generating" small class="mr-1" />{{ generating ? '正在生成' : '生成并覆盖当天历史' }}
        </b-button>
        <b-button variant="outline-primary" size="sm" class="ml-2" :disabled="loading" @click="loadSummary">刷新查看</b-button>
      </b-form>
      <small class="hint">生成只会覆盖所选日期的核心股快照，不修改题材评分和回测结果。</small>
    </b-card>

    <b-alert v-if="message" show variant="success">{{ message }}</b-alert>
    <b-alert v-if="error" show variant="danger">{{ error }}</b-alert>

    <section v-if="summary" class="metrics">
      <article><small>交易日</small><b>{{ summary.trade_date }}</b></article>
      <article><small>有核心股题材</small><b>{{ summary.topic_count }}</b></article>
      <article><small>快照间隔</small><b>{{ generatedInterval }} 分钟</b></article>
      <article><small>生成范围</small><b>{{ generatedRange }}</b></article>
    </section>

    <b-card v-if="summary && summary.topics.length" class="result-card" no-body>
      <div class="topic-toolbar">
        <div>
          <label>查看题材</label>
          <b-form-select v-model="selectedTopicId" :options="topicOptions" size="sm" @change="loadDetails" />
        </div>
        <span v-if="selectedTopicStat">
          {{ selectedTopicStat.snapshot_count }} 个时点 · {{ selectedTopicStat.core_stock_rows }} 条记录 ·
          {{ selectedTopicStat.first_snapshot }}—{{ selectedTopicStat.last_snapshot }}
        </span>
      </div>
      <div class="table-responsive">
        <table class="history-table">
          <thead>
            <tr><th>快照时间</th><th>变化</th><th>排名</th><th>核心股</th><th>核心分</th><th>当时涨幅</th><th>当时价格</th><th>角色</th><th>入选依据</th></tr>
          </thead>
          <tbody>
            <tr v-for="row in historyRows" :key="row.core_calculated_at + '-' + row.code">
              <td>{{ row.showTime ? row.snapshot_minute : '' }}</td>
              <td><b-badge v-if="row.showTime && row.snapshotChanged" variant="warning">换核</b-badge></td>
              <td>#{{ row.core_rank }}</td>
              <td><strong>{{ row.name }}</strong> <small>{{ row.code }}</small></td>
              <td>{{ number(row.core_score) }}</td>
              <td :class="numberClass(row.pct)">{{ pct(row.pct) }}</td>
              <td>{{ row.price ? '¥' + Number(row.price).toFixed(2) : '--' }}</td>
              <td>{{ row.role }}</td>
              <td><span v-for="reason in row.reasons" :key="reason" class="reason-chip">{{ reason }}</span></td>
            </tr>
            <tr v-if="!historyRows.length"><td colspan="9" class="empty">该题材当天没有达到核心分门槛的记录</td></tr>
          </tbody>
        </table>
      </div>
    </b-card>
    <div v-else-if="summary && !loading" class="empty-state">该日期尚未生成核心股历史。</div>
  </div>
</template>

<script>
import { request_zzquant } from '@/network/request'

function isoDate(date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export default {
  name: 'WonderTopicCoreHistory',
  data() {
    return {
      tradeDate: isoDate(new Date()),
      intervalMinutes: 5,
      coreLimit: 2,
      intervalOptions: [5, 10, 15, 30].map(value => ({ value, text: `${value} 分钟` })),
      generating: false,
      loading: false,
      error: '',
      message: '',
      summary: null,
      generation: null,
      selectedTopicId: null,
      items: []
    }
  },
  computed: {
    topicOptions() {
      return (this.summary ? this.summary.topics : []).map(item => ({ value: item.topic_id, text: item.topic_name }))
    },
    selectedTopicStat() {
      return this.summary && this.summary.topics.find(item => Number(item.topic_id) === Number(this.selectedTopicId))
    },
    generatedInterval() { return (this.generation && this.generation.interval_minutes) || this.intervalMinutes },
    generatedRange() {
      if (this.generation) return `${this.generation.first_snapshot}—${this.generation.last_snapshot}`
      const topics = (this.summary && this.summary.topics) || []
      return topics.length ? `${topics[0].first_snapshot}—${topics[0].last_snapshot}` : '--'
    },
    historyRows() {
      const groups = {}
      this.items.forEach(item => {
        if (!groups[item.core_calculated_at]) groups[item.core_calculated_at] = []
        groups[item.core_calculated_at].push(item)
      })
      let previousCodes = ''
      const snapshots = Object.keys(groups).sort().map(key => {
        const rows = groups[key].sort((a, b) => a.core_rank - b.core_rank)
        const codes = rows.map(row => row.code).join(',')
        const changed = previousCodes !== '' && codes !== previousCodes
        previousCodes = codes
        return rows.map((row, index) => ({ ...row, showTime: index === 0, snapshotChanged: changed }))
      })
      return snapshots.reverse().reduce((all, rows) => all.concat(rows), [])
    }
  },
  mounted() { this.loadSummary() },
  methods: {
    pct(value) { if (value === null || value === undefined) return '--'; const number = Number(value); return `${number > 0 ? '+' : ''}${number.toFixed(2)}%` },
    number(value) { return value === null || value === undefined ? '--' : Number(value).toFixed(1) },
    numberClass(value) { return Number(value) > 0 ? 'up' : Number(value) < 0 ? 'down' : '' },
    errorText(error, fallback) {
      return (error && error.response && error.response.data && (error.response.data.detail || error.response.data.message)) || error.message || fallback
    },
    async generateHistory() {
      this.generating = true
      this.error = ''
      this.message = ''
      try {
        const response = await request_zzquant({
          url: '/v3/topic/core-stocks/history/generate', method: 'POST', timeout: 300000, noRedirect: true,
          data: { trade_date: this.tradeDate, interval_minutes: this.intervalMinutes, core_limit: this.coreLimit }
        })
        const body = response && response.data
        if (!body || body.code !== 20000) throw new Error((body && body.message) || '生成失败')
        this.generation = body.data
        this.message = `已生成 ${body.data.snapshot_count} 个时点、${body.data.topic_count} 个题材、${body.data.core_stock_rows} 条核心股记录。`
        await this.loadSummary(true)
      } catch (error) {
        this.error = this.errorText(error, '核心股历史生成失败')
      } finally { this.generating = false }
    },
    async loadSummary(keepMessage) {
      this.loading = true
      this.error = ''
      if (!keepMessage) this.message = ''
      try {
        const response = await request_zzquant({ url: '/v3/topic/core-stocks/history', method: 'GET', params: { trade_date: this.tradeDate }, noRedirect: true })
        const body = response && response.data
        if (!body || body.code !== 20000) throw new Error((body && body.message) || '查询失败')
        this.summary = body.data
        if (!body.data.topics.some(item => Number(item.topic_id) === Number(this.selectedTopicId))) {
          this.selectedTopicId = body.data.topics.length ? body.data.topics[0].topic_id : null
        }
        if (this.selectedTopicId) await this.loadDetails()
        else this.items = []
      } catch (error) {
        this.error = this.errorText(error, '核心股历史查询失败')
      } finally { this.loading = false }
    },
    async loadDetails() {
      if (!this.selectedTopicId) return
      try {
        const response = await request_zzquant({
          url: '/v3/topic/core-stocks/history', method: 'GET', noRedirect: true,
          params: { trade_date: this.tradeDate, topic_id: this.selectedTopicId }
        })
        const body = response && response.data
        if (!body || body.code !== 20000) throw new Error((body && body.message) || '查询失败')
        this.items = body.data.items || []
      } catch (error) {
        this.error = this.errorText(error, '题材核心股明细查询失败')
      }
    }
  }
}
</script>

<style scoped>
.history-page { padding:12px 10px 30px; color:var(--text-main,#263445); }
.page-heading { display:flex; align-items:flex-end; justify-content:space-between; gap:12px; margin-bottom:12px; }
.page-heading h4 { margin:0; font-weight:700; }
.page-heading p { margin:4px 0 0; color:var(--text-muted,#718096); font-size:12px; }
.control-card,.result-card { margin-bottom:14px; border-color:var(--border-color,#dfe5eb); border-radius:8px; }
.interval-select { width:100px; }
.limit-input { width:70px; }
.hint { display:block; margin-top:8px; color:var(--text-muted,#718096); }
.metrics { display:grid; grid-template-columns:repeat(4,minmax(120px,1fr)); gap:8px; margin-bottom:12px; }
.metrics article { padding:10px 12px; border:1px solid var(--border-color,#dfe5eb); border-radius:7px; background:var(--bg-card,#fff); }
.metrics small { display:block; color:var(--text-muted,#718096); font-size:10px; }
.metrics b { display:block; margin-top:3px; font-size:15px; }
.topic-toolbar { display:flex; align-items:flex-end; justify-content:space-between; gap:16px; padding:10px 14px; border-bottom:1px solid var(--border-color,#e8edf2); }
.topic-toolbar > div { width:min(360px,100%); }
.topic-toolbar label { display:block; margin-bottom:3px; color:var(--text-muted,#718096); font-size:11px; }
.topic-toolbar span { color:var(--text-muted,#718096); font-size:11px; }
.history-table { width:100%; min-width:950px; border-collapse:collapse; font-size:11px; }
.history-table th,.history-table td { padding:8px 10px; border-bottom:1px solid var(--border-color,#edf0f3); text-align:left; white-space:nowrap; }
.history-table th { color:var(--text-muted,#718096); background:var(--bg-muted,#f6f8fa); }
.history-table small { color:var(--text-muted,#718096); }
.reason-chip { display:inline-block; margin-right:4px; padding:2px 5px; border-radius:4px; background:var(--bg-muted,#edf2f6); }
.up { color:#c53030; }
.down { color:#276749; }
.empty,.empty-state { padding:32px; text-align:center; color:var(--text-muted,#718096); }
@media(max-width:768px) {
  .control-card form { display:grid; grid-template-columns:auto 1fr; gap:8px; align-items:center; }
  .control-card form > * { margin:0!important; width:100%; }
  .metrics { grid-template-columns:repeat(2,minmax(0,1fr)); }
  .topic-toolbar { flex-direction:column; align-items:flex-start; }
}
</style>
